/**
 * Web CV Health score gauge: the twin of `HealthScoreGauge.tsx`.
 *
 * A half-circle on a 100 x 50 box (radius 45, stroke 10, flat ends on the
 * baseline) with a three-stop gradient whose hue follows the score's
 * strength, and the bare score under the arc. No "/100" and no percent sign:
 * assistive technology reads "72 out of 100" from the meter instead.
 *
 * Motion (`HEALTH_GAUGE_MOTION`): a first result waits 400 ms, sweeps the arc
 * over 1000 ms and slides each digit up into place; a changed score under the
 * same `historyKey` moves number and arc together from the old value over
 * 420 ms. The accessible value is always the real score. Reduced motion and a
 * hidden tab draw the result at once.
 */
import { useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HEALTH_GAUGE_MID_STOP,
  HEALTH_GAUGE_MOTION,
  HEALTH_GAUGE_PATH,
  HEALTH_GAUGE_STOPS,
  easeOutCubic,
  gaugeDashOffset,
  getScoreStrength,
} from './health-gauge-geometry';
import type { HealthScoreGaugeProps } from './health-props';

export type { HealthScoreGaugeProps };

/** The last score each surface drew, so a change interpolates instead of replaying. */
const scoreHistory = new Map<string, number>();

const cubic = (points: readonly number[]) => `cubic-bezier(${points.join(',')})`;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function HealthScoreGauge({
  score,
  historyKey,
  animate = true,
  fromScore,
}: HealthScoreGaugeProps) {
  const { t } = useTranslation('common');
  const gradientId = `tabbio-health-gauge-${useId().replace(/:/g, '')}`;
  const arcRef = useRef<SVGPathElement>(null);
  const numberRef = useRef<HTMLSpanElement>(null);
  const value = Math.max(0, Math.min(100, Math.round(Number.isFinite(score) ? score : 0)));
  /** The number on screen while a changed score moves; null shows `value`. */
  const [moving, setMoving] = useState<number | null>(null);
  const strength = getScoreStrength(value) ?? 'weak';
  const stops = HEALTH_GAUGE_STOPS[strength];
  const digits = String(moving ?? value).split('');

  // Decide the motion before paint, so the first frame is never the end state.
  useLayoutEffect(() => {
    const arc = arcRef.current;
    const number = numberRef.current;
    if (!arc || !number) return;
    // Nothing drawn under the key yet: start from the caller's `fromScore`.
    const before =
      (historyKey ? scoreHistory.get(historyKey) : undefined) ??
      (typeof fromScore === 'number' ? Math.round(fromScore) : undefined);
    if (historyKey) scoreHistory.set(historyKey, value);
    const still =
      !animate ||
      prefersReducedMotion() ||
      (typeof document !== 'undefined' && document.hidden) ||
      typeof arc.animate !== 'function';
    if (still) return;

    if (before === undefined || before === value) {
      const motion = HEALTH_GAUGE_MOTION;
      const sweep = arc.animate(
        [{ strokeDashoffset: 100 }, { strokeDashoffset: gaugeDashOffset(value) }],
        {
          duration: motion.drawMs,
          delay: motion.drawDelayMs,
          easing: cubic(motion.drawEasing),
          fill: 'backwards',
        },
      );
      const slides = Array.from(number.children).map((digit, index) =>
        (digit as HTMLElement).animate(
          [
            { transform: 'translateY(110%)', opacity: 0 },
            { transform: 'translateY(0)', opacity: 1 },
          ],
          {
            duration: motion.digitMs + index * motion.digitExtraMs,
            delay: motion.digitDelayMs + index * motion.digitStaggerMs,
            easing: cubic(motion.digitEasing),
            fill: 'backwards',
          },
        ),
      );
      return () => {
        sweep.cancel();
        for (const slide of slides) slide.cancel();
      };
    }

    // A changed score: number and arc move together from the old value.
    let frame = 0;
    const started = performance.now();
    const draw = (now: number) => {
      const progress = Math.min(1, (now - started) / HEALTH_GAUGE_MOTION.updateMs);
      const current = before + (value - before) * easeOutCubic(progress);
      arc.style.strokeDashoffset = String(gaugeDashOffset(current));
      if (progress < 1) {
        setMoving(Math.round(current));
        frame = requestAnimationFrame(draw);
      } else {
        arc.style.strokeDashoffset = '';
        setMoving(null);
      }
    };
    arc.style.strokeDashoffset = String(gaugeDashOffset(before));
    setMoving(before);
    frame = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(frame);
      arc.style.strokeDashoffset = '';
      setMoving(null);
    };
  }, [animate, fromScore, historyKey, value]);

  return (
    <div
      className="tabbio-health-gauge"
      data-strength={strength}
      role="meter"
      aria-label={t('cvHealth.report.gaugeLabel')}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={value}
      aria-valuetext={t('cvHealth.report.gaugeValue', { score: value })}
    >
      <svg viewBox="0 0 100 50" fill="none" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor={stops.light} />
            <stop offset={HEALTH_GAUGE_MID_STOP} stopColor={stops.mid} />
            <stop offset="1" stopColor={stops.deep} />
          </linearGradient>
        </defs>
        <path className="tabbio-health-gauge-track" d={HEALTH_GAUGE_PATH} pathLength={100} />
        <path
          ref={arcRef}
          className="tabbio-health-gauge-arc"
          d={HEALTH_GAUGE_PATH}
          pathLength={100}
          stroke={`url(#${gradientId})`}
          strokeDasharray="100"
          strokeDashoffset={gaugeDashOffset(value)}
        />
      </svg>
      <span ref={numberRef} className="tabbio-health-gauge-value" aria-hidden="true">
        {digits.map((digit, index) => (
          // Digits repeat ("100"), so position is the identity.
          <span key={index}>{digit}</span>
        ))}
      </span>
    </div>
  );
}

export default HealthScoreGauge;
